import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { S, Icon, BtnPrimary, BtnSecondary } from '../regureStyles';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

// 2-step: upload → result. The backend auto-detects column names tolerantly
// (see FIELD_ALIASES in clientController.js) so we skip the manual column-map
// step unless auto-detection proves insufficient in practice.
const ImportClientsModal = ({ onClose, onDone }) => {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const resp = await axios.post(`${BASE_URL}/clients/import`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(resp.data?.data || resp.data);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || t('clientsModule.import.errImport'));
    } finally {
      setUploading(false);
    }
  };

  const handleFile = (f) => {
    if (!f) return;
    const lower = (f.name || '').toLowerCase();
    if (!lower.endsWith('.csv') && !lower.endsWith('.xlsx') && !lower.endsWith('.xls')) {
      setError(t('clientsModule.import.errFileType'));
      return;
    }
    setError('');
    setFile(f);
  };

  const handleDone = () => {
    setFile(null);
    setResult(null);
    onDone && onDone();
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)' }}
      onClick={onClose}
    >
      <div
        style={{ width: 520, maxWidth: '92vw', maxHeight: '90vh', background: '#fff', borderRadius: 12, boxShadow: '0 24px 64px rgba(0,0,0,0.2)', padding: 28, overflowY: 'auto' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary }}>{t('clientsModule.import.title')}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <Icon name='x' color={S.textMuted} size={20} />
          </button>
        </div>

        {!result && (
          <>
            <div style={{ fontSize: 13, color: S.textSecondary, marginBottom: 14 }}>
              {t('clientsModule.import.descriptionPrefix')}
              <strong>{t('clientsModule.import.csvBold')}</strong>
              {t('clientsModule.import.orText')}
              <strong>{t('clientsModule.import.xlsxBold')}</strong>
              {t('clientsModule.import.descriptionSuffix')}
            </div>

            <label
              htmlFor='client-import-file'
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => {
                e.preventDefault();
                setDragOver(false);
                const f = e.dataTransfer.files?.[0];
                handleFile(f);
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 28,
                border: `2px dashed ${dragOver ? S.burgundy : S.borderLight}`,
                borderRadius: 10,
                background: dragOver ? 'rgba(128,0,32,0.04)' : S.bgCard,
                cursor: 'pointer',
                marginBottom: 14,
              }}
            >
              <Icon name='upload' color={S.textMuted} size={28} />
              <div style={{ marginTop: 10, fontSize: 13, fontWeight: 600, color: S.textPrimary }}>
                {file ? file.name : t('clientsModule.import.dropzoneHint')}
              </div>
              {file && (
                <div style={{ marginTop: 4, fontSize: 12, color: S.textMuted }}>{t('clientsModule.import.sizeKb', { size: Math.round(file.size / 1024) })}</div>
              )}
              <input
                id='client-import-file'
                type='file'
                accept='.csv,.xlsx,.xls'
                onChange={e => handleFile(e.target.files?.[0])}
                style={{ display: 'none' }}
              />
            </label>

            {error && (
              <div style={{ padding: 10, background: '#fee2e2', color: '#991b1b', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{error}</div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <BtnSecondary onClick={onClose} style={{ flex: 1 }}>{t('clientsModule.import.cancel')}</BtnSecondary>
              <BtnPrimary onClick={handleUpload} disabled={!file || uploading} style={{ flex: 1 }}>
                {uploading ? t('clientsModule.import.importing') : t('clientsModule.import.import')}
              </BtnPrimary>
            </div>
          </>
        )}

        {result && (
          <>
            <div style={{ padding: 16, background: '#d1fae5', color: '#065f46', borderRadius: 8, fontSize: 14, marginBottom: 14 }}>
              {result.imported === 1
                ? t('clientsModule.import.importedOne', { count: result.imported })
                : t('clientsModule.import.importedOther', { count: result.imported })}
              {result.skipped > 0 && (result.skipped === 1
                ? t('clientsModule.import.skippedOne', { count: result.skipped })
                : t('clientsModule.import.skippedOther', { count: result.skipped }))}
            </div>

            {result.errors && result.errors.length > 0 && (
              <details style={{ marginBottom: 14 }}>
                <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: S.textPrimary, marginBottom: 8 }}>
                  {result.errors.length === 1
                    ? t('clientsModule.import.errorsCountOne', { count: result.errors.length })
                    : t('clientsModule.import.errorsCountOther', { count: result.errors.length })}
                </summary>
                <div style={{ maxHeight: 180, overflowY: 'auto', background: S.bgCard, borderRadius: 8, padding: 10, fontSize: 12, color: S.textSecondary }}>
                  {result.errors.map((err, i) => (
                    <div key={i} style={{ paddingBottom: 6, marginBottom: 6, borderBottom: i < result.errors.length - 1 ? `1px solid ${S.borderLight}` : 'none' }}>
                      {t('clientsModule.import.rowError', { row: err.row, reason: err.reason })}
                    </div>
                  ))}
                </div>
              </details>
            )}

            <BtnPrimary onClick={handleDone} style={{ width: '100%' }}>{t('clientsModule.import.done')}</BtnPrimary>
          </>
        )}
      </div>
    </div>
  );
};

export default ImportClientsModal;
